import { signal } from '@angular/core';

/**
 * Changements en attente sur un fichier traduisible, par langue : un fichier
 * choisi mais pas encore envoyé, ou un retrait demandé.
 *
 * L'état vit chez le parent (et non dans `app-translated-file-upload`) pour
 * deux raisons : la barre d'onglets a besoin de savoir, pour chaque langue, si
 * elle a un fichier — c'est ce qui déclenche le triangle d'alerte, comme pour
 * les textes — et la zone de dépôt de l'onglet actif est recréée à chaque
 * changement d'onglet.
 */
export class TranslatedFilesState {
  private pending = signal<{ [locale: string]: File }>({});
  private removed = signal<string[]>([]);

  readonly files = this.pending.asReadonly();
  readonly removedLocales = this.removed.asReadonly();

  set(locale: string, file: File): void {
    this.pending.update((files) => ({ ...files, [locale]: file }));
    this.removed.update((locales) => locales.filter((l) => l !== locale));
  }

  /**
   * Vide la langue : annule le fichier en attente et, s'il en existait un
   * enregistré, marque son retrait pour le prochain enregistrement.
   */
  clearLocale(locale: string, hadSavedFile: boolean): void {
    this.pending.update((files) => {
      const { [locale]: _removed, ...rest } = files;
      return rest;
    });
    this.removed.update((locales) =>
      hadSavedFile && !locales.includes(locale) ? [...locales, locale] : locales,
    );
  }

  clear(): void {
    this.pending.set({});
    this.removed.set([]);
  }

  isEmpty(): boolean {
    return (
      Object.keys(this.pending()).length === 0 && this.removed().length === 0
    );
  }

  /**
   * Fichier à montrer dans la zone de dépôt : celui en attente, sinon l'URL de
   * celui déjà enregistré — sauf si son retrait vient d'être demandé.
   */
  displayedFiles(
    locale: string,
    currentUrls: { [locale: string]: string },
  ): Array<File | string> {
    const pending = this.pending()[locale];
    if (pending) {
      return [pending];
    }

    if (this.removed().includes(locale) || !currentUrls[locale]) {
      return [];
    }

    return [currentUrls[locale]];
  }

  /**
   * Valeurs à passer à `app-translation-tabs` : une langue sans fichier propre
   * (ni enregistré, ni en attente) fait apparaître le triangle d'alerte.
   */
  tabValues(
    locales: string[],
    currentUrls: { [locale: string]: string },
  ): { [locale: string]: unknown[] } {
    return Object.fromEntries(
      locales.map((locale) => [
        locale,
        [this.displayedFiles(locale, currentUrls)[0]],
      ]),
    );
  }
}
